/**
 * Value rendering components for audit log expanded details.
 */

import React from 'react';
import { Box, Text, Stack, Code, Link, CodeBlock, Icon, Tooltip, Grid } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  formatAttributeName,
  formatPermissionValue,
  formatRoleName,
  formatValue,
  shouldUseCodeStyle,
  shouldUseMonospace,
  isNullableDbField,
  extractNullableDbValue,
  isJSONString,
} from '../../utils/formatting';
import { isCampaignMappedField } from '../../utils/campaign-mapping';
import { SegmentLinkWithPreview, ConditionsInline, type MessageInfo } from '../segment-conditions-preview';
import {
  detectCodeLanguage,
  isCodeOrHtmlString,
  generateCodeBlockOverrideCSS,
  CODE_BLOCK_PREVIEW_LINES,
} from '../../utils/code-detection';
import { isEmptyString } from '@/utils/string';
import { appendMasqParam } from '@/utils/masq';
import { getUserDisplayNameWithEmail, type UserWithName } from '../../utils/user-formatting';
import { CODE_BLOCK_THEME } from '../../constants/code-block-theme';
import { CAMPAIGN_OBJECT_TYPES } from '../../constants/shared';
import { isPermissionField, isPermissionString } from './field-helpers';
import { formatDisplayValue } from './format-display-value';

// Field names that contain filter/condition structures
const FILTER_FIELD_NAMES = new Set([
  'filters',
  'filter',
  'conditions',
  'trigger',
  'exit_conditions',
  'exitconditions',
  'global_exit_conditions',
  'globalexitconditions',
  'conversion_event_filters',
  'conversioneventfilters',
  'trigger_filters',
  'triggerfilters',
  'entry_filters',
  'entryfilters',
  'event_filters',
  'eventfilters',
  'attribute_filters',
  'attributefilters',
  'recipients', // Newsletter recipients contain segments_negatable filters
  'segment_groups', // Newsletter segment_groups contain segments_negatable filters
  'segmentgroups',
]);

// Field names that contain conversion objects with nested event_name
const CONVERSION_FIELD_NAMES = new Set(['conversion', 'conversionevent', 'conversion_event']);

/**
 * Reorder an array for column-first display in a CSS Grid.
 *
 * CSS Grid fills items row-by-row, but we want to display items column-first (A-Z down each column).
 * This function reorders the array so that when CSS Grid places items row-by-row,
 * reading column-by-column gives the original sorted order.
 *
 * Example with 6 items [A,B,C,D,E,F] and 3 columns:
 * - Desired visual (reading columns top-to-bottom): A C E
 *                                                   B D F
 * - CSS Grid row order needed: [A, C, E, B, D, F]
 *
 * Example with 4 items [A,B,C,D] and 3 columns:
 * - Desired visual: A C D
 *                   B
 * - CSS Grid row order needed: [A, C, D, B]
 *
 * @param items - The sorted array to reorder
 * @param numColumns - Number of columns in the grid
 * @returns Reordered array for CSS Grid row-by-row placement
 */
function reorderForColumnFirstGrid<T>(items: T[], numColumns: number): T[] {
  if (items.length === 0 || numColumns <= 1) {
    return items;
  }

  const numRows = Math.ceil(items.length / numColumns);
  // Calculate how many columns have the full numRows items
  // vs how many have (numRows - 1) items
  const remainder = items.length % numColumns;
  const fullColumns = remainder === 0 ? numColumns : remainder;

  const result: T[] = [];

  for (let row = 0; row < numRows; row++) {
    for (let col = 0; col < numColumns; col++) {
      // Calculate the index in the original sorted array
      // Columns 0 to (fullColumns-1) have numRows items
      // Columns fullColumns to (numColumns-1) have (numRows-1) items
      let index: number;
      if (col < fullColumns) {
        // Full column: index = col * numRows + row
        index = col * numRows + row;
      } else {
        // Short column: only has (numRows - 1) items, skip if row is the last row
        if (row >= numRows - 1) {
          continue;
        }
        // Items in full columns + items in previous short columns + current row
        index = fullColumns * numRows + (col - fullColumns) * (numRows - 1) + row;
      }

      if (index < items.length) {
        result.push(items[index]);
      }
    }
  }

  return result;
}

/**
 * Check if a value looks like a filter/condition object (has and/or/not structure or filter properties).
 * Also recognizes arrays of filter objects (e.g., segment_groups for newsletters).
 */
function isFilterObject(value: unknown): boolean {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  // Handle arrays of filter objects (e.g., segment_groups: [{ type: 'segments_negatable', ... }])
  if (Array.isArray(value)) {
    // Check if it's a non-empty array where items look like filter objects
    if (value.length > 0) {
      return value.every((item) => {
        if (item == null || typeof item !== 'object' || Array.isArray(item)) {
          return false;
        }
        const itemObj = item as Record<string, unknown>;
        // Check for known condition types used in newsletters and campaigns
        // Types from ~/code/ui/app/utils/condition-types.ts
        const knownConditionTypes = [
          'segments_negatable',
          'segment',
          'attribute',
          'event_attribute',
          'message', // Message interaction conditions
          'event',
          'pageview',
          'screenview',
          'form',
          'device_attribute',
          'newsletter_recipients',
          'inbound',
          'opt_out',
          'relationship',
          'object_attribute',
        ];
        const itemType = itemObj.type as string | undefined;
        return (
          (itemType != null && knownConditionTypes.includes(itemType)) ||
          'and' in itemObj ||
          'or' in itemObj ||
          'segment_id' in itemObj ||
          'event' in itemObj || // Message conditions have { event: {...} }
          ('field' in itemObj && 'operator' in itemObj)
        );
      });
    }
    return false;
  }

  const obj = value as Record<string, unknown>;
  return (
    'and' in obj ||
    'or' in obj ||
    'not' in obj ||
    'segment' in obj ||
    'segment_id' in obj || // Segment conditions format
    'condition_id' in obj || // Condition reference format
    'event_name' in obj ||
    'event' in obj || // Message conditions
    obj.type === 'segments_negatable' || // Newsletter recipients format
    obj.type === 'message' || // Message interaction conditions
    obj.type === 'attribute' ||
    ('field' in obj && 'operator' in obj) ||
    ('attribute' in obj && 'operator' in obj)
  );
}

/**
 * Check if a value looks like a conversion object with nested event_name.
 */
function isConversionObject(value: unknown): boolean {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return 'event_name' in obj && typeof obj.event_name === 'object';
}

/**
 * Format a conversion object that contains an event_name.
 */
function formatConversionObject(value: unknown): string {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return formatValue(value);
  }
  const obj = value as Record<string, unknown>;
  if ('event_name' in obj && typeof obj.event_name === 'object' && obj.event_name != null) {
    const eventName = obj.event_name as Record<string, unknown>;
    const name = eventName.name as string | undefined;
    const etype = eventName.etype as string | undefined;

    if (name != null) {
      // Don't try to decode short strings that look like regular words
      let displayName = name;
      if (/[+/=]/.test(name) || name.length > 20) {
        try {
          const decoded = atob(name);
          const isPrintable = /^[\x20-\x7E\u00A0-\uFFFF]*$/.test(decoded);
          if (isPrintable && btoa(decoded) === name) {
            displayName = decoded;
          }
        } catch {
          // Keep original
        }
      }

      if (!isEmptyString(etype)) {
        return `Event "${displayName}" (${etype})`;
      }
      return `Event "${displayName}"`;
    }
  }
  // Use formatValue for proper JSON serialization instead of String() which produces "[object Object]"
  return formatValue(value);
}

// Layout constants
export const ATTRIBUTE_LABEL_MIN_WIDTH = '120px';
export const NESTED_OBJECT_MIN_WIDTH = '100px';

/**
 * Render filter text with clickable condition and segment references.
 * Converts "Condition #X" and "segment #X" patterns into clickable links that open filtered audit logs in a new tab.
 * When segments list is provided, displays segment names alongside IDs.
 */
export function renderFilterTextWithLinks(
  text: string,
  context: 'account' | 'workspace',
  contextId: number,
  startTime?: string,
  endTime?: string,
  segments: Array<{ id: number; name: string }> = [],
): React.ReactNode {
  // Build a map for quick segment name lookup
  const segmentMap = new Map(segments.map((s) => [s.id, s.name]));

  // Combined pattern to match:
  // - "Condition #X" or "NOT matching condition #X" (for conditions)
  // - "In segment #X" or "NOT in segment #X" (for segment references)
  const referencePattern = /((?:NOT matching )?[Cc]ondition #(\d+))|((?:NOT )?[Ii]n segment #(\d+))/g;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Build base URL params
  const basePath = context === 'workspace' ? `/workspaces/${contextId}/settings/audit-logs` : `/settings/audit-logs`;
  const dateParams =
    startTime != null && endTime != null
      ? `&start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}`
      : '';
  // Helper to build filter URL with masq param for account context
  const buildFilterUrl = (params: string) =>
    context === 'workspace' ? `${basePath}${params}` : appendMasqParam(`${basePath}${params}`);

  while ((match = referencePattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Check which pattern matched
    if (match[1] != null) {
      // Condition match: match[1] is full text, match[2] is condition ID
      const fullMatch = match[1];
      const conditionId = match[2];
      const filterUrl = buildFilterUrl(`?object_type=Condition&object_id=${conditionId}${dateParams}`);

      parts.push(
        <Tooltip
          key={`condition-${conditionId}-${match.index}`}
          content={`Click to view audit history for Segment Condition #${conditionId}`}
          placement="top"
        >
          <Link
            href={filterUrl}
            variant="secondary"
            isUnderlined={true}
            target="_blank"
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
          >
            {fullMatch}
          </Link>
        </Tooltip>,
      );
    } else if (match[3] != null) {
      // Segment match: match[3] is full text, match[4] is segment ID
      const fullMatch = match[3];
      const segmentId = match[4];
      const segmentIdNum = Number.parseInt(segmentId, 10);
      const segmentName = segmentMap.get(segmentIdNum);
      const filterUrl = buildFilterUrl(`?object_type=Segment&object_id=${segmentId}${dateParams}`);

      // Display format: "In segment Name (#ID)" or "NOT in segment Name (#ID)" if name is available
      // Otherwise fall back to original format: "In segment #ID"
      let displayText = fullMatch;
      if (segmentName != null) {
        // Replace "#ID" with "Name (#ID)"
        displayText = fullMatch.replace(`#${segmentId}`, `${segmentName} (#${segmentId})`);
      }

      const tooltipContent = `Click to view audit history for Segment${
        segmentName != null ? ` "${segmentName}"` : ''
      } #${segmentId}`;

      // Use SegmentLinkWithPreview for workspace context to show segment conditions on hover
      if (context === 'workspace' && contextId > 0) {
        parts.push(
          <SegmentLinkWithPreview
            key={`segment-${segmentId}-${match.index}`}
            segmentId={segmentIdNum}
            segmentName={segmentName}
            href={filterUrl}
            displayText={displayText}
            workspaceId={contextId}
          />,
        );
      } else {
        // Fallback to simple tooltip + link for account context
        parts.push(
          <Tooltip key={`segment-${segmentId}-${match.index}`} content={tooltipContent} placement="top">
            <Link
              href={filterUrl}
              variant="secondary"
              isUnderlined={true}
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer' }}
            >
              {displayText}
            </Link>
          </Tooltip>,
        );
      }
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // If no matches found, return the original text
  if (parts.length === 0) {
    return text;
  }

  return <>{parts}</>;
}

// Type for simple attribute
export interface SimpleAttribute {
  key: string;
  name: string;
  value: string;
  rawValue: unknown;
  isRedacted: boolean;
  isBoolean: boolean;
  isEmail: boolean;
  useCodeStyle: boolean;
  isIpAddress?: boolean;
}

/**
 * User info for audit log lookups.
 * Shared type for user data used across expanded details rendering.
 * Fields are optional except user_id/email since API responses may have partial data.
 */
export interface UserInfo {
  user_id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  deleted?: boolean;
}

/**
 * Try to render a special user field (user_id in permissions, created_by/updated_by in Asset).
 * Returns a React element if the field should be rendered specially, or null if standard rendering applies.
 */
export function tryRenderUserField(
  attr: SimpleAttribute,
  objectType: string,
  allUsers: UserInfo[] | undefined,
): React.ReactNode | null {
  // Special handling for user_id in permission-related object types
  const isUserIdInPermissions =
    attr.key === 'user_id' && (objectType === 'UserAccountPermissions' || objectType === 'WorkspacePermissions');

  if (isUserIdInPermissions && typeof attr.rawValue === 'number' && allUsers != null) {
    const user = allUsers.find((u) => u.user_id === attr.rawValue);
    if (user != null) {
      const displayName = getUserDisplayNameWithEmail(user as UserWithName);
      return (
        <Box key={attr.key} display="flex" alignItems="baseline" gap="100">
          <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
            Team Member:
          </Text>
          <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
            {displayName}
          </Text>
        </Box>
      );
    }
  }

  // Special handling for created_by/updated_by in Asset and DesignStudioNode objects
  const isUserField =
    (objectType === 'Asset' || objectType === 'DesignStudioNode') &&
    (attr.key === 'created_by' || attr.key === 'updated_by');
  if (isUserField && allUsers != null) {
    let userId: number | null = null;

    // Handle nullable DB format (for Asset)
    if (isNullableDbField(attr.rawValue)) {
      const extracted = extractNullableDbValue(attr.rawValue as { string?: string; int64?: number; valid: boolean });
      if (typeof extracted === 'number') {
        userId = extracted;
      }
    }
    // Handle plain number format (for DesignStudioNode only)
    else if (objectType === 'DesignStudioNode' && typeof attr.rawValue === 'number') {
      userId = attr.rawValue;
    }

    if (userId != null && userId > 0) {
      const user = allUsers.find((u) => u.user_id === userId);
      if (user != null) {
        const displayName = getUserDisplayNameWithEmail(user as UserWithName);
        const label = attr.key === 'created_by' ? 'Created By' : 'Updated By';
        return (
          <Box key={attr.key} display="flex" alignItems="baseline" gap="100">
            <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
              {label}:
            </Text>
            <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
              {displayName}
            </Text>
          </Box>
        );
      }
    }
  }

  return null;
}

// Options for rendering IP address with filter link
export interface IpAddressFilterOptions {
  context: 'account' | 'workspace';
  contextId: number;
  startTime?: string;
  endTime?: string;
}

// Type for code modal state
export type CodeModalState = { name: string; code: string; language: string } | null;

/**
 * Render a single attribute value with appropriate styling.
 * @param attr - The attribute to render
 * @param ipFilterOptions - Optional options for rendering IP address filter button
 */
export function renderValue(attr: SimpleAttribute, ipFilterOptions?: IpAddressFilterOptions) {
  // Wrap value in a container that allows wrapping but prefers single line
  // Common styles to ensure vertical alignment with attribute names
  const alignmentStyle = { display: 'inline', verticalAlign: 'baseline', wordBreak: 'break-word' as const };

  if (attr.isRedacted) {
    return (
      <Box display="flex" alignItems="center" gap="050" style={{ wordBreak: 'break-word' }}>
        <Icon name="view-off" size="xs" color="subtle" />
        <Text size="s" color="subtle">
          [REDACTED]
        </Text>
      </Box>
    );
  }

  // IP address with filter button
  if (attr.isIpAddress === true && ipFilterOptions != null) {
    const { context, contextId, startTime, endTime } = ipFilterOptions;
    const basePath = context === 'workspace' ? `/workspaces/${contextId}/settings/audit-logs` : `/settings/audit-logs`;
    const dateParams =
      startTime != null && endTime != null
        ? `&start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}`
        : '';
    const rawFilterUrl = `${basePath}?ip_addr=${encodeURIComponent(attr.value)}${dateParams}`;
    const filterUrl = context === 'workspace' ? rawFilterUrl : appendMasqParam(rawFilterUrl);

    return (
      <Box display="inline-flex" alignItems="center" gap="100">
        <Text size="s" color="subtle">
          {attr.value}
        </Text>
        <Tooltip content="Filter logs by this IP address">
          <Box as="a" href={filterUrl} display="inline-flex" alignItems="center">
            <Icon name="filter" size="xs" color="subtle" />
          </Box>
        </Tooltip>
      </Box>
    );
  }

  if (attr.isEmail) {
    return (
      <Link
        href={`mailto:${attr.value}`}
        variant="secondary"
        isUnderlined={false}
        withSafeExternalAttributes={false}
        style={alignmentStyle}
      >
        {attr.value}
      </Link>
    );
  }
  if (attr.useCodeStyle) {
    return (
      <Code size="s" style={alignmentStyle}>
        {attr.value}
      </Code>
    );
  }
  return (
    <Text size="s" color="subtle" style={alignmentStyle}>
      {attr.value}
    </Text>
  );
}

/**
 * Render a single object's properties (for array items).
 */
export function renderObjectProperties(
  obj: Record<string, unknown>,
  objectType: string,
  prefix = '',
  excludedFields: Set<string> = new Set(),
  allUsers?: Array<{ user_id: number; email: string; first_name: string; last_name: string; deleted: boolean }>,
) {
  const isCampaignObject = CAMPAIGN_OBJECT_TYPES.has(objectType);

  // For ProfileMerge, only show the 'id' field from nested objects
  const fieldsToExclude =
    objectType === 'ProfileMerge'
      ? new Set(
          Object.keys(obj)
            .filter((k) => k.toLowerCase() !== 'id')
            .map((k) => k.toLowerCase()),
        )
      : excludedFields;

  return Object.entries(obj)
    .filter(([key]) => !fieldsToExclude.has(key.toLowerCase()))
    .sort(([a], [b]) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map(([key, value]) => {
      // Special handling for Asset and DesignStudioNode created_by/updated_by fields
      if (
        (objectType === 'Asset' || objectType === 'DesignStudioNode') &&
        (key === 'created_by' || key === 'updated_by') &&
        allUsers != null
      ) {
        let userId: number | null = null;

        // Handle nullable DB format (for Asset)
        if (isNullableDbField(value)) {
          const extracted = extractNullableDbValue(value);
          if (typeof extracted === 'number') {
            userId = extracted;
          }
        }
        // Handle plain number format (for DesignStudioNode only)
        else if (objectType === 'DesignStudioNode' && typeof value === 'number') {
          userId = value;
        }

        if (userId != null && userId > 0) {
          const user = allUsers.find((u) => u.user_id === userId);
          if (user != null) {
            const displayValue = getUserDisplayNameWithEmail(user as UserWithName);
            const label = key === 'created_by' ? 'Created By' : 'Updated By';
            return (
              <Box key={`${prefix}${key}`} display="flex" alignItems="baseline" gap="100">
                <Text size="s" weight="medium" style={{ minWidth: NESTED_OBJECT_MIN_WIDTH, flexShrink: 0 }}>
                  {label}:
                </Text>
                <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
                  {displayValue}
                </Text>
              </Box>
            );
          }
        }
      }

      const formattedName = formatAttributeName(key);

      const formattedValue = formatDisplayValue(value, key, objectType);
      const isBoolean = typeof value === 'boolean';
      const isNullOrEmpty = value == null || value === '';
      // For campaign objects with mapped fields, don't use code style (they have semantic labels)
      const isCampaignMapped = isCampaignObject && isCampaignMappedField(key);
      const useCode =
        !isNullOrEmpty &&
        !isBoolean &&
        !isCampaignMapped &&
        (shouldUseCodeStyle(formattedName) || shouldUseMonospace(value, key));

      return (
        <Box key={`${prefix}${key}`} display="flex" alignItems="baseline" gap="100">
          <Text size="s" weight="medium" style={{ minWidth: NESTED_OBJECT_MIN_WIDTH, flexShrink: 0 }}>
            {formattedName}:
          </Text>
          {useCode ? (
            <Code size="s">{formattedValue}</Code>
          ) : (
            <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
              {formattedValue}
            </Text>
          )}
        </Box>
      );
    });
}

interface RoleDefinition {
  id: number;
  name: string;
}

interface RenderRolePropertiesParams {
  roleItem: Record<string, unknown>;
  prefix: string;
  objectType: string;
  roleDefinitions?: Record<number, RoleDefinition> | null;
  allUsers?: Array<{ user_id: number; email: string; first_name: string; last_name: string; deleted: boolean }>;
  workspaces?: Array<{ id: number; name: string }>;
}

/**
 * Render properties of a role object, replacing user_id with team member name
 * and role_id with role name. Extracted to module level to avoid recreation on each render.
 */
function renderRoleProperties({
  roleItem,
  prefix,
  objectType,
  roleDefinitions,
  allUsers,
  workspaces = [],
}: RenderRolePropertiesParams): React.ReactNode[] {
  // Filter out role_id if we're showing the friendly role name
  const entries = Object.entries(roleItem).filter(([propKey]) => {
    // Skip role_id if we have role definitions (we'll show the friendly name via role_id lookup)
    if (propKey === 'role_id' && roleDefinitions != null) return false;
    // Skip the raw 'role' field if we have role definitions (we'll show the friendly name instead)
    if (propKey === 'role' && roleDefinitions != null) return false;
    return true;
  });

  const result: React.ReactNode[] = [];

  // If we have role definitions, show the friendly role name first
  const roleId = roleItem.role_id as number | undefined;
  if (roleDefinitions != null && roleId != null && roleDefinitions[roleId] != null) {
    // Format standard role names (e.g., 'author-redact-sensitive' -> 'Author with sensitive attributes hidden')
    const friendlyRoleName = formatRoleName(roleDefinitions[roleId].name);
    result.push(
      <Box key={`${prefix}role-name-friendly`} display="flex" alignItems="baseline" gap="100">
        <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
          Role:
        </Text>
        <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
          {friendlyRoleName}
        </Text>
      </Box>,
    );
  } else if (roleItem.role != null) {
    // Fallback to raw role name if no role definitions - still format it
    const friendlyRoleName = formatRoleName(String(roleItem.role));
    result.push(
      <Box key={`${prefix}role-name-raw`} display="flex" alignItems="baseline" gap="100">
        <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
          Role:
        </Text>
        <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
          {friendlyRoleName}
        </Text>
      </Box>,
    );
  }

  for (const [propKey, propValue] of entries) {
    const propName = formatAttributeName(propKey);

    // Replace user_id with team member name/email
    if (propKey === 'user_id' && typeof propValue === 'number' && allUsers != null) {
      const user = allUsers.find((u) => u.user_id === propValue);
      if (user != null) {
        const displayValue = getUserDisplayNameWithEmail(user as UserWithName);
        result.push(
          <Box key={`${prefix}${propKey}`} display="flex" alignItems="baseline" gap="100">
            <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
              Team Member:
            </Text>
            <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
              {displayValue}
            </Text>
          </Box>,
        );
        continue;
      }
    }

    // Replace environment_id with workspace name
    // Use Number() comparison to handle string/number type mismatches
    if (propKey === 'environment_id' && typeof propValue === 'number' && workspaces.length > 0) {
      const workspace = workspaces.find((w) => Number(w.id) === propValue);
      if (workspace != null) {
        result.push(
          <Box key={`${prefix}${propKey}`} display="flex" alignItems="baseline" gap="100">
            <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
              Workspace:
            </Text>
            <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
              {workspace.name}
            </Text>
          </Box>,
        );
        continue;
      }
    }

    // Default rendering for other properties
    const formattedValue = formatDisplayValue(propValue, propKey, objectType);
    result.push(
      <Box key={`${prefix}${propKey}`} display="flex" alignItems="baseline" gap="100">
        <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
          {propName}:
        </Text>
        <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
          {formattedValue}
        </Text>
      </Box>,
    );
  }

  return result;
}

interface RelatedLogEntry {
  id: number;
  object_type: string;
  object_id: string;
  data: unknown;
}

interface RenderComplexValueProps {
  value: unknown;
  name: string;
  key: string;
  objectType: string;
  onOpenCodeModal: (data: CodeModalState) => void;
  context?: 'account' | 'workspace';
  contextId?: number;
  startTime?: string;
  endTime?: string;
  allUsers?: Array<{ user_id: number; email: string; first_name: string; last_name: string; deleted: boolean }>;
  roleDefinitions?: Record<number, RoleDefinition> | null;
  segments?: Array<{ id: number; name: string }>;
  workspaces?: Array<{ id: number; name: string }>;
  /** Pre-fetched related logs (condition details) from the backend */
  relatedLogs?: RelatedLogEntry[];
  /** Optional campaigns list for looking up campaign names in message conditions */
  campaigns?: Array<{ id: number; name: string }>;
  /** Optional form ID to name mappings (from segment's linked_form_id -> name) */
  formNames?: Record<string, string>;
  /** Optional object types for relationship conditions */
  objectTypes?: Array<{ id: number; name: string }>;
  /** Optional newsletters list for looking up newsletter names in newsletter_recipients conditions */
  newsletters?: Array<{ id: number; name: string }>;
  /** Optional messages list for looking up message names by output_id (e.g., "output_5") */
  messages?: MessageInfo[];
}

/**
 * Render complex values (arrays, nested objects, code strings).
 */
export function renderComplexValue({
  value,
  name,
  key,
  objectType,
  onOpenCodeModal,
  context = 'workspace',
  contextId = 0,
  startTime: _startTime,
  endTime: _endTime,
  allUsers,
  roleDefinitions,
  segments = [],
  workspaces = [],
  relatedLogs,
  campaigns = [],
  formNames,
  objectTypes,
  newsletters = [],
  messages = [],
}: RenderComplexValueProps) {
  // Handle nullable DB fields with JSON content - parse and render as object
  if (isNullableDbField(value)) {
    const innerValue = extractNullableDbValue(value);
    if (innerValue == null) {
      // Invalid/null - render as simple value
      return (
        <Box key={name} display="flex" alignItems="baseline" gap="100">
          <Text size="s" weight="medium" style={{ minWidth: '120px', flexShrink: 0 }}>
            {name}:
          </Text>
          <Text size="s" color="subtle">
            —
          </Text>
        </Box>
      );
    }
    // If the inner value is a JSON string, parse and render appropriately
    if (typeof innerValue === 'string' && isJSONString(innerValue)) {
      try {
        const parsed: unknown = JSON.parse(innerValue);
        // Handle arrays differently from objects
        if (Array.isArray(parsed)) {
          // For arrays, recursively render the array
          return renderComplexValue({
            value: parsed,
            name,
            key,
            objectType,
            onOpenCodeModal,
            context,
            contextId,
            allUsers,
            roleDefinitions,
            segments: _segments,
            workspaces,
            relatedLogs,
            campaigns,
            formNames,
            objectTypes,
            newsletters,
            messages,
          });
        }
        // For objects/arrays, render appropriately
        if (typeof parsed === 'object' && parsed !== null) {
          // Arrays should be rendered using renderComplexValue which handles them properly
          // (avoids showing numeric indices "0", "1", etc. as field names)
          if (Array.isArray(parsed)) {
            return renderComplexValue({
              value: parsed,
              name,
              key,
              objectType,
              onOpenCodeModal,
              context,
              contextId,
              allUsers,
              roleDefinitions,
              segments: _segments,
              workspaces,
              relatedLogs,
              campaigns,
              formNames,
              objectTypes,
              newsletters,
              messages,
            });
          }
          // For objects, render as key-value pairs
          return (
            <Box key={name}>
              <Text size="s" weight="medium" style={{ marginBottom: '8px' }}>
                {name}:
              </Text>
              <Box
                p="200"
                style={{
                  borderRadius: themeVars.radius['4'],
                  border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
                }}
              >
                <Stack gap="050">
                  {renderObjectProperties(parsed as Record<string, unknown>, objectType, '', new Set(), allUsers)}
                </Stack>
              </Box>
            </Box>
          );
        }
        // For primitives, fall through to render as string
      } catch {
        // If parsing fails, fall through to render as string
      }
    }
    // For non-JSON inner values, recursively render
    return renderComplexValue({
      value: innerValue,
      name,
      key,
      objectType,
      onOpenCodeModal,
      context,
      contextId,
      allUsers,
      roleDefinitions,
      segments: _segments,
      workspaces,
      relatedLogs,
      campaigns,
      formNames,
      objectTypes,
      newsletters,
      messages,
    });
  }

  // Code/HTML/template strings - display preview with option to view full in modal
  if (typeof value === 'string' && isCodeOrHtmlString(value)) {
    const lines = value.split('\n');
    const lineCount = lines.length;
    const needsModal = lineCount > CODE_BLOCK_PREVIEW_LINES;
    const language = detectCodeLanguage(value);
    const codeBlockId = `code-block-${key}`;

    // Always show preview (first N lines)
    const previewValue = needsModal ? lines.slice(0, CODE_BLOCK_PREVIEW_LINES).join('\n') : value;

    return (
      <Box key={key}>
        <Text size="s" weight="medium" style={{ marginBottom: '8px' }}>
          {name}:
        </Text>
        <style>{generateCodeBlockOverrideCSS(codeBlockId, CODE_BLOCK_THEME.background, CODE_BLOCK_THEME.text)}</style>
        <CodeBlock
          data-codeblock-id={codeBlockId}
          code={previewValue}
          language={language}
          mode="dark"
          darkTheme="night-owl"
          withCopyButton={!needsModal}
          withLineNumbers={false}
          style={{
            backgroundColor: CODE_BLOCK_THEME.background,
          }}
        />
        {needsModal ? (
          <Box pt="150" display="flex" alignItems="center" gap="100">
            <button
              type="button"
              onClick={() => onOpenCodeModal({ name, code: value, language })}
              style={{
                background: 'none',
                border: `1px solid ${themeVars.color['border-base']}`,
                borderRadius: themeVars.radius['4'],
                padding: '4px 8px',
                cursor: 'pointer',
                color: themeVars.color['text-base'],
                fontSize: '13px',
              }}
            >
              View all {lineCount} lines
            </button>
            <Text size="xs" color="subtle">
              ({lineCount - CODE_BLOCK_PREVIEW_LINES} more lines hidden)
            </Text>
          </Box>
        ) : null}
      </Box>
    );
  }

  if (Array.isArray(value)) {
    // Check if array contains plain objects (not nested arrays)
    const hasObjects =
      value.length > 0 && value.every((item) => item != null && typeof item === 'object' && !Array.isArray(item));

    if (hasObjects) {
      const normalizedKey = key.toLowerCase().replace(/[_-]/g, '');

      // Check if this is an array of filter conditions (e.g., segment_groups for newsletters)
      if (FILTER_FIELD_NAMES.has(normalizedKey) && isFilterObject(value)) {
        // Wrap the array in an { and: [...] } structure for ConditionsInline
        // This allows multiple segment_groups to be displayed as "Matches all of N conditions"
        const wrappedConditions: Record<string, unknown> =
          value.length === 1 ? (value[0] as Record<string, unknown>) : { and: value };

        return (
          <Box key={name}>
            <Text size="s" weight="medium" style={{ marginBottom: '8px' }}>
              {name}:
            </Text>
            <ConditionsInline
              conditions={wrappedConditions}
              workspaceId={context === 'workspace' ? contextId : undefined}
              relatedLogs={relatedLogs}
              campaigns={campaigns}
              segments={segments}
              formNames={formNames}
              objectTypes={objectTypes}
              newsletters={newsletters}
              messages={messages}
            />
          </Box>
        );
      }

      // Check if this is a roles array (for WorkspacePermissions)
      const isRolesArray = key.toLowerCase() === 'roles';

      if (isRolesArray) {
        // Sort roles alphabetically by role name
        const sortedRoles = [...value].sort((a, b) => {
          const roleA = String((a as Record<string, unknown>).role ?? '').toLowerCase();
          const roleB = String((b as Record<string, unknown>).role ?? '').toLowerCase();
          return roleA.localeCompare(roleB);
        });

        // Reorder for column-first display in a 2-column grid
        // Input order: [A, B, C, D, E, F] (sorted A-Z)
        // Desired visual layout:
        //   A  C
        //   B  D
        // CSS Grid row order needed: [A, C, B, D]
        const numColumns = 2;
        const columnFirstOrder = reorderForColumnFirstGrid(sortedRoles, numColumns);

        return (
          <Box key={name}>
            <Text size="s" weight="medium" style={{ marginBottom: '8px' }}>
              {name}:
            </Text>
            <Grid columns={2} gap="200">
              {columnFirstOrder.map((item, index) => (
                <Box
                  key={index}
                  p="200"
                  style={{
                    borderRadius: themeVars.radius['4'],
                    border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
                  }}
                >
                  <Stack gap="050">
                    {renderRoleProperties({
                      roleItem: item as Record<string, unknown>,
                      prefix: `${index}-`,
                      objectType,
                      roleDefinitions,
                      allUsers,
                      workspaces,
                    })}
                  </Stack>
                </Box>
              ))}
            </Grid>
          </Box>
        );
      }

      // Default: render objects in vertical stack
      return (
        <Box key={name}>
          <Text size="s" weight="medium" style={{ marginBottom: '8px' }}>
            {name}:
          </Text>
          <Stack gap="200">
            {value.map((item, index) => (
              <Box
                key={index}
                p="200"
                style={{
                  borderRadius: themeVars.radius['4'],
                  border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
                }}
              >
                <Stack gap="050">
                  {renderObjectProperties(
                    item as Record<string, unknown>,
                    objectType,
                    `${index}-`,
                    new Set(),
                    allUsers,
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      );
    }

    // Simple array (strings, numbers, etc.)
    // Check if this is a permission array - format each item specially
    // Note: Must check length > 0 because Array.every() returns true for empty arrays (vacuous truth)
    const isPermissionArray =
      value.length > 0 &&
      (isPermissionField(key) || value.every((v) => typeof v === 'string' && isPermissionString(v)));

    if (isPermissionArray) {
      // Render permission array as vertical list with formatted permission values
      return (
        <Box key={name}>
          <Text size="s" weight="medium" style={{ marginBottom: '8px' }}>
            {name}:
          </Text>
          <Stack gap="050">
            {value.map((item, index) => (
              <Text key={index} size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
                •{' '}
                {typeof item === 'string' && isPermissionString(item)
                  ? formatPermissionValue(item)
                  : formatDisplayValue(item, key, objectType)}
              </Text>
            ))}
          </Stack>
        </Box>
      );
    }

    return (
      <Box key={name} display="flex" alignItems="baseline" gap="100">
        <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
          {name}:
        </Text>
        <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
          {(() => {
            const joined = value.map((v) => formatDisplayValue(v, key, objectType)).join(', ');
            return !isEmptyString(joined) ? joined : '—';
          })()}
        </Text>
      </Box>
    );
  }

  // Nested object
  if (value != null && typeof value === 'object') {
    const normalizedKey = key.toLowerCase().replace(/[_-]/g, '');

    // For filter/trigger fields, show the visual conditions tree directly inline
    if (FILTER_FIELD_NAMES.has(normalizedKey) && isFilterObject(value)) {
      return (
        <Box key={name}>
          <Text size="s" weight="medium" style={{ marginBottom: '8px' }}>
            {name}:
          </Text>
          <ConditionsInline
            conditions={value as Record<string, unknown>}
            workspaceId={context === 'workspace' ? contextId : undefined}
            relatedLogs={relatedLogs}
            campaigns={campaigns}
            segments={segments}
            formNames={formNames}
            objectTypes={objectTypes}
            newsletters={newsletters}
            messages={messages}
          />
        </Box>
      );
    }

    // For conversion fields, format the nested event_name
    if (CONVERSION_FIELD_NAMES.has(normalizedKey) && isConversionObject(value)) {
      const formatted = formatConversionObject(value);
      return (
        <Box key={name} display="flex" alignItems="baseline" gap="100">
          <Text size="s" weight="medium" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH, flexShrink: 0 }}>
            {name}:
          </Text>
          <Text size="s" color="subtle" style={{ wordBreak: 'break-word' }}>
            {formatted}
          </Text>
        </Box>
      );
    }

    // Default: render as nested object with properties
    return (
      <Box key={name}>
        <Text size="s" weight="medium" style={{ marginBottom: '8px' }}>
          {name}:
        </Text>
        <Box
          p="200"
          style={{
            borderRadius: themeVars.radius['4'],
            border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
          }}
        >
          <Stack gap="050">
            {renderObjectProperties(value as Record<string, unknown>, objectType, '', new Set(), allUsers)}
          </Stack>
        </Box>
      </Box>
    );
  }

  return null;
}
